import getLastRedactedFile from '@helpers/getLastRedactedFile';
import { has, maxBy } from 'lodash';
import { createSelector } from 'reselect';

import { selectCurrentRoutePayload } from '@common-modules/routing';
import { ContentType, DetailTDO, UDRsPolyAsset, ViewSettings } from '../models';
import { defaultState, namespace } from '../store';
import {
  CasepointIntegration,
  DeletedOverlaysCache,
  DetectionAssetState,
  ExternalIntegration,
  ExterroIntegration,
  FOIAXpressIntegration,
  NuixIntegration,
} from '../store.models';
import { TDOId } from '@common-modules/universal/models/Brands';
import { CollectionWrapper } from '@common/web-worker';
import type { StatusHistoryNoteOutcome } from '@common-modules/statusHistory/models';

// export interface LocalState {
//   [namespace]: DetectionAssetState &
//     ViewSettings & { fetchingTdo: boolean } & { [param: string]: any };
// }

export interface LocalState {
  [namespace]: DetectionAssetState &
    ViewSettings & { fetchingTdo: boolean } & {
      /** VE-30516: see `selectTdoSettingsFromServer`. */
      tdoSettingsFromServer?: boolean;
      mediaDetailsPageSelectedTab: string;
      redactedAssetStarted: boolean;
      buttonTranscriptionDisable: boolean;
      speakerSeparationFetchSettled: boolean;
      gettingRedactionJobStatus: boolean;
      redactionJobStatus: string;
      redactionJobCreatedDateTime: string | null;
      tdoIsChanged: boolean;
      saveRev: number;
      isSaveRunning: boolean;
      isBannerDismissed: boolean;
      pendingLockedStatus: string | null;
      pendingLockedStatusNote: string | null;
      pendingLockedStatusResponse: string | null;
      pendingLockedStatusResponseType: StatusHistoryNoteOutcome | null;
      isShowConfirmLeave: boolean;
      /** What the route's `confirmLeave` returned; see leaveSignals.ts. */
      message?: string;
      /**
       * The createJob response; `targetId` is the TDO the job runs against
       * (VE-28061: the completion poll reports for it after the editor closes).
       */
      redactionJob: { id: string; targetId?: TDOId } | null;
      originalFileStatus: string; // TODO rename to redactionStatus or similar
      canLeave: boolean;
      isHoldingShift: boolean;
      udrCollection: CollectionWrapper;
      deletedOverlaysCache: DeletedOverlaysCache;
      detectObjects: {
        headObjects: boolean;
        personObjects: boolean;
      };
      showDetectOptionsDialog: boolean;
      udrsState: {
        localUDRAsset: UDRsPolyAsset;
        selectedUDRGroupId: string | undefined;
      };
      buttonDetectFaceDisable: boolean;
      integrations: {
        govQa: {
          sessionId?: string;
          isFormShown: boolean;
          isSendingFile: boolean;
          isStatusDialogShown: boolean;
        };
        foiaXpress: FOIAXpressIntegration;
        casepoint: CasepointIntegration;
        exterro: ExterroIntegration;
        nuix: NuixIntegration;
        externalIntegration: ExternalIntegration;
      };
      downloadUrl: string | null;
      pollingOpticalTrackingAreProcessing: boolean;
      downloadStarted: boolean;
      pollingDownloadDone: boolean;
      numberOfRedactedFile: number | undefined;
      auditLogs: unknown;
      tdoLocks: { id: string }[];
    };
}

export const local = (state: LocalState) =>
  state?.[namespace] ||
  (defaultState as unknown as LocalState[typeof namespace]);
const EMPTY_TAGS: never[] = [];

export const selectCurrentTdoId = createSelector(
  selectCurrentRoutePayload,
  (payload) => payload.tdoId as TDOId | undefined
);
export const selectIsTdoExternallyModified = createSelector(
  local,
  (s) => s.isTdoExternallyModified
);
export const selectHasInvalidTdoData = createSelector(
  local,
  (s) => s.hasInvalidTdoData
);

export const selectTdo = createSelector(local, (s) => s && s.tdo);

// IMAGE-TODO why do we have this selector and selectContentTypeHasImage ?
export const selectIsImage = createSelector(selectTdo, (tdo) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const contentType =
    tdo?.primaryAsset?.contentType ??
    (tdo?.details?.veritoneFile as any)?.contentType;

  if (contentType) {
    return /^image\//.test(contentType);
  }

  const mimetype = tdo?.details?.veritoneFile?.mimetype;
  if (mimetype && /^image\//.test(mimetype)) {
    return true;
  }

  const filename = tdo?.details?.veritoneFile?.filename ?? tdo?.name;
  if (filename) {
    return /\.(jpe?g|png|gif|bmp|webp|svg|tiff?)$/i.test(filename);
  }

  return false;
});

export const selectIsTdoReady = createSelector(
  selectTdo,
  (tdo) => tdo?.status === 'recorded' && tdo.primaryAsset !== null
);

export const selectPrimaryAsset = createSelector(
  selectTdo,
  (tdo) => tdo?.primaryAsset || null
);

export const selectThumbnailAssets = createSelector(
  selectTdo,
  (tdo) => tdo?.thumbnailAssets || null
);

export const selectTranscriptAssets = createSelector(
  selectTdo,
  (tdo) => tdo?.transcriptAssets || null
);

export const selectContentType = createSelector(
  selectPrimaryAsset,
  (primaryAsset) => {
    if (!primaryAsset || !primaryAsset.contentType) {
      return ContentType.AUDIO_VIDEO;
    }
    const type = primaryAsset.contentType.split('/')[0];
    if (type === 'audio') {
      return ContentType.AUDIO;
    }
    if (type === 'image') {
      return ContentType.IMAGE;
    }
    return ContentType.AUDIO_VIDEO;
  }
);

export const selectContentTypeHasVideo = createSelector(
  selectContentType,
  (contentType: ContentType) =>
    ContentType.AUDIO_VIDEO === contentType || ContentType.VIDEO === contentType
);

export const selectContentTypeHasAudio = createSelector(
  selectContentType,
  (contentType) =>
    ContentType.AUDIO === contentType || ContentType.AUDIO_VIDEO === contentType
);

export const selectContentTypeHasImage = createSelector(
  selectContentType,
  (contentType) => ContentType.IMAGE === contentType
);

export const selectTdoAreLoading = (state: LocalState) =>
  local(state).fetchingTdo;

/**
 * VE-30516: the TDO came from the server with its own `details.settings`.
 *
 * The seed gate. Deliberately NOT `settings.isSaved`: that flag lives inside the
 * object it describes, so every settings writer could clobber it — and one did,
 * which made already-configured files look unseeded and get overwritten with the
 * org default profile.
 */
export const selectTdoSettingsFromServer = (state: LocalState): boolean =>
  !!local(state).tdoSettingsFromServer;

export const selectCurrentPosition = createSelector(
  local,
  (s) => s.currentPosition
);

/**
 * The playhead sample immediately before `selectCurrentPosition`. Pair the two to
 * ask about the interval that just played rather than a single instant — see
 * `selectPlayheadActiveGroupIds` for why that distinction matters (VE-25876).
 */
export const selectPreviousPosition = createSelector(
  local,
  (s) => s.previousPosition
);

export const selectMediaDetailsPage = (state: LocalState) =>
  local(state).mediaDetailsPageSelectedTab;

export const getRedactedMediaCount = (tdo: DetailTDO | null) =>
  tdo?.redactedMediaAssets?.records?.length || 0;

/**
 * The audit log describing the newest redacted output, or `null` when the one
 * we hold describes an older output.
 *
 * VE-27735: matched by `details.redactedMediaAssetId` across the whole list, not
 * by position. The list is server-ordered newest-first, but `records[0]` is not
 * reliably the newest here: `FETCH_REDACTED_MEDIA_SUCCESS` prepends records the
 * server hasn't propagated yet (see the merge in reducers.ts), so a stale record
 * can sit at the front and mask the matching one further down. Reading index 0
 * alone then failed closed and hid the Download Audit Log button.
 */
export const getLastAuditLog = (tdo: DetailTDO | null) => {
  if (!tdo || !has(tdo, 'auditLogAssets.records')) {
    return null;
  }

  const auditRecords = tdo?.auditLogAssets?.records ?? [];
  const auditAsset = auditRecords[0];
  if (!auditAsset) {
    return null;
  }

  // No redacted output to match against — weird state; hand back what we have.
  const redactedFile = getLastRedactedFile(tdo);
  if (!redactedFile) {
    return auditAsset;
  }

  const linkedAsset = auditRecords.find(
    (record) => record.details?.['redactedMediaAssetId'] === redactedFile.id
  );
  if (linkedAsset) {
    return linkedAsset;
  }

  // Nothing names the current output. An asset carrying no
  // `redactedMediaAssetId` predates the linkage and still counts; one that names
  // a *different* output does not.
  const auditDetails = auditAsset.details;
  if (!auditDetails || !auditDetails['redactedMediaAssetId']) {
    return auditAsset;
  }
  return null;
};

export const getLastRedactExport = (tdo: DetailTDO | null) => {
  // check that export is for most recent redacted-media asset
  const exportAsset = tdo?.redactExportAssets?.records?.[0];
  if (!exportAsset) {
    return null;
  }
  const exportDetails = exportAsset.details;

  const redactedFile = getLastRedactedFile(tdo);
  // if no tdoDetails must be an older asset - if no redactedFile in a weird state
  if (
    !exportDetails ||
    !exportDetails.tdos ||
    !exportDetails.tdos[tdo.id] ||
    !redactedFile
  ) {
    return exportAsset;
  }

  if (exportDetails.tdos[tdo.id]['redactedMediaAssetId'] === redactedFile.id) {
    return exportAsset;
  } else {
    return null;
  }
};

export const pickLastCompletedTaskByCreatedDate = <
  T extends {
    status: string;
    createdDateTime: string | null;
  },
>(
  tasks: T[]
) => {
  if (!tasks) {
    return null;
  }

  const tasksComplete = tasks.filter((item) => item.status === 'complete');

  if (tasksComplete.length === 0) {
    return { createdDateTime: 0 };
  }

  return maxBy(tasksComplete, 'createdDateTime');
};

export const pickLastCompletedTaskByCompletedDate = <
  T extends {
    status: string;
    completedDateTime?: string | null;
  },
>(
  tasks: T[]
) => {
  if (!tasks) {
    return null;
  }

  const tasksComplete = tasks.filter((task) => task.status === 'complete');

  if (tasksComplete.length === 0) {
    return { completedDateTime: 0 };
  }

  return maxBy(tasksComplete, 'completedDateTime');
};

export const selectIsLoading = (state: LocalState) => {
  if (local(state).loaded) {
    return false;
  } else if (!local(state).tdo) {
    return true;
  }
};

export const selectIsLoaded = (state: LocalState) => local(state).loaded;

export const selectDetails = (state: LocalState) =>
  ((tdo) => tdo?.details)(local(state).tdo);

// legacy videos / and audio only don't have frameRate
export const selectFrameRate = (state: LocalState) =>
  ((tdo) => tdo?.details?.veritoneFile?.videoFrameRate)(local(state).tdo);

export const selectTags = (state: LocalState) =>
  ((tdo) => tdo?.details?.tags ?? EMPTY_TAGS)(local(state).tdo);

export const selectRedactedAssetStarted = (state: LocalState) =>
  local(state).redactedAssetStarted;

export const gettingRedactionJobStatusAreLoading = (state: LocalState) =>
  local(state).gettingRedactionJobStatus;

export const selectGettingRedactionJobStatus = (state: LocalState) =>
  local(state).gettingRedactionJobStatus;

export const selectRedactionJob = (state: LocalState) =>
  local(state).redactionJob;

export const selectRedactionJobStatus = (state: LocalState) =>
  local(state).redactionJobStatus;

export const selectRedactionJobCreatedDateTime = (state: LocalState) =>
  local(state).redactionJobCreatedDateTime;

export const selectNumberOfRedactedFile = (state: any) =>
  local(state).numberOfRedactedFile;

export const selectTdoIsChanged = (state: LocalState) =>
  local(state).tdoIsChanged || local(state).isSaveRunning;

export const selectIsSaveRunning = (state: LocalState) =>
  local(state).isSaveRunning;

export const selectIsBannerDismissed = (state: LocalState) =>
  local(state).isBannerDismissed;

export const selectPendingLockedStatus = (state: LocalState) =>
  local(state).pendingLockedStatus;

export const selectPendingLockedStatusNote = (state: LocalState) =>
  local(state).pendingLockedStatusNote;

export const selectPendingLockedStatusResponse = (state: LocalState) =>
  local(state).pendingLockedStatusResponse;

export const selectPendingLockedStatusResponseType = (state: LocalState) =>
  local(state).pendingLockedStatusResponseType;

export const selectIsShowConfirmLeave = (state: LocalState) =>
  local(state).isShowConfirmLeave;

export const selectCanLeave = (state: LocalState) => local(state).canLeave;

/** The pending leave's message — tells Discard where the reviewer was going (VE-30855). */
export const selectConfirmLeaveMessage = (state: LocalState) =>
  local(state).message;

export const selectOriginalFileStatus = (state: LocalState) =>
  local(state).originalFileStatus;

export const selectIsHoldingShift = (state: LocalState) =>
  local(state).isHoldingShift;

export const selectUdrAsset = (state: LocalState) =>
  local(state).udrsState.localUDRAsset;

export const selectUdrCollection = (state: LocalState) =>
  local(state).udrCollection;

export const selectSelectedUDRGroupId = (state: LocalState) =>
  local(state).udrsState.selectedUDRGroupId;

export const selectDisableFacesButton = (state: LocalState) =>
  local(state).buttonDetectFaceDisable;

export const selectDisableTranscriptionButton = (state: LocalState) =>
  local(state).buttonTranscriptionDisable;

/** See the store field. Reset on media switch; set when the worker reports. */
export const selectSpeakerSeparationFetchSettled = (state: LocalState) =>
  local(state).speakerSeparationFetchSettled;

export const selectGovQASessionId = (state: LocalState) =>
  local(state).integrations.govQa.sessionId;

export const selectGovQAState = (state: LocalState) =>
  local(state).integrations.govQa;

export const selectDeletedOverlaysCache = (state: LocalState) =>
  local(state).deletedOverlaysCache;

export const selectFOIAXpressState = (state: LocalState) =>
  local(state).integrations.foiaXpress;

export const selectCasepointState = (state: LocalState) =>
  local(state).integrations.casepoint;

export const selectExterroState = (state: LocalState) =>
  local(state).integrations.exterro;

export const selectNuixState = (state: LocalState) =>
  local(state).integrations.nuix;

export const selectExternalIntegrationState = (state: LocalState) =>
  local(state).integrations.externalIntegration;

export const selectDownloadUrl = (state: LocalState) =>
  local(state).downloadUrl;

export const selectSelectedBoundingPolyGroup = (state: LocalState) =>
  local(state).selectedPolyGroups;

export const selectDetectionSeriesLengthWithDeleted = (state: LocalState) =>
  local(state).detectionSeriesLengthWithDeleted;

export const selectDetectObjects = (state: LocalState) =>
  local(state).detectObjects;

export const selectShowDetectOptionsDialog = (state: LocalState) =>
  local(state).showDetectOptionsDialog;

export const selectVoiceMaskMediaUrl = (state: LocalState) => {
  const localState = local(state);
  return localState && 'voiceMaskMediaUrl' in localState
    ? localState.voiceMaskMediaUrl
    : undefined;
};

export const selectTdoLocks = (state: LocalState) => local(state).tdoLocks;
